import { useState } from 'react';
import { aboutInfo } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 亚洲精品一区二区',
  description: aboutInfo.brandIntro,
  url: 'https://taimudaojia.cn/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '亚洲精品一区二区',
    url: 'https://taimudaojia.cn',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://taimudaojia.cn' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://taimudaojia.cn/about' },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://taimudaojia.cn/api/form/d9tvmetca23r0tr1ank0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SchemaOrg data={aboutSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <main className="pt-20">
        <div className="bg-background-100/70 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              关于我们
            </h1>
          </div>
        </div>

        <section className="py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <div className="w-16 h-1 rounded-full bg-primary-500 mb-6"></div>
              <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
                {aboutInfo.brandIntro}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    使命
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{aboutInfo.mission}</p>
                </div>
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    愿景
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{aboutInfo.vision}</p>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                核心价值观
              </h2>
              <div className="w-16 h-1 rounded-full bg-accent-500 mb-6"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {aboutInfo.values.map((v, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-background-100 border border-background-200/70 p-6"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-check-double-line text-lg"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                      {v.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                平台口碑
              </h2>
              <div className="w-16 h-1 rounded-full bg-primary-500 mb-6"></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {aboutInfo.reputation.map((r, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-background-100 border border-background-200/70 p-6 text-center"
                  >
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-1">
                      {r.metric}
                    </p>
                    <p className="text-foreground-600 text-xs md:text-sm">{r.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background-100/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-600 text-sm">有任何问题或建议，欢迎与我们取得联系</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              <div className="md:col-span-2 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">联系电话</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">公司地址</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">微信公众号</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>

              <div className="md:col-span-3">
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="rounded-xl bg-background-50 border border-background-200/70 p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入咨询主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  <div className="honeypot-field">
                    <label htmlFor="website_alt">Website</label>
                    <input
                      type="text"
                      id="website_alt"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                  >
                    提交留言
                  </button>

                  {formStatus === 'success' && (
                    <div className="rounded-lg bg-accent-100 border border-accent-200/70 px-4 py-3">
                      <p className="text-sm text-accent-800 font-medium">
                        感谢您的留言！我们会尽快与您取得联系。
                      </p>
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                      <p className="text-sm text-red-700 font-medium">
                        {formError || '提交失败，请稍后重试。'}
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}